// components/CheckoutModal.jsx

import React, { useState } from 'react';
import { api } from '../api/client';
import { X, Lock, CreditCard, Mail } from 'lucide-react';

export default function CheckoutModal({ app, isOpen, onClose }) {
    const [email, setEmail] = useState('');
    const [isGuest, setIsGuest] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

        const handleCheckout = async () => {
            setLoading(true);
            setError('');

        try {
            let response;
            
            if (isGuest && email) {
                // Guest checkout
                response = await api.post('/api/stripe/create-guest-checkout-session', {
                    app_id: app.id,
                    email: email
                });
            } else {
                // Authenticated checkout
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('Please login to continue');
                    setLoading(false);
                    return;
                }

                // VALIDATE FIRST - Check if user can purchase
                try {
                    await api.post('/api/purchases/validate', null, {
                        params: { app_id: app.id }
                    });
                } catch (validationError) {
                    if (validationError.response?.status === 403) {
                        setError("You cannot purchase your own app!");
                    } else if (validationError.response?.status === 400) {
                        setError("You have already purchased this app!");
                    } else {
                        setError(validationError.response?.data?.detail || "Unable to validate purchase");
                    }
                    setLoading(false);
                    return;
                }
                
                // Proceed with checkout after validation passes
                response = await api.post('/api/stripe/create-checkout-session', {
                    app_id: app.id
                });
            }

            if (response.data.checkout_url) {
                // Redirect to Stripe checkout
                window.location.href = response.data.checkout_url;
            }
        } catch (err) {
            console.error('Checkout error:', err);
            
            if (err.response?.status === 401) {
                setError('Session expired. Please login again.');
                // Save checkout context
                sessionStorage.setItem('checkoutAppId', app.id);
                sessionStorage.setItem('checkoutAppSlug', app.slug);
                // Redirect to login with return URL
                window.location.href = `/login?redirect=/marketplace/app/${app.slug}&checkout=true`;
            } else if (err.response?.data?.detail) {
                setError(err.response.data.detail);
            } else {
                setError('Failed to start checkout. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = () => {
        // Save checkout context
        sessionStorage.setItem('checkoutAppId', app.id);
        sessionStorage.setItem('checkoutAppSlug', app.slug);
        // Redirect to login with checkout flag
        window.location.href = `/login?redirect=/marketplace/app/${app.slug}&checkout=true`;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-2xl font-bold mb-6">Complete Your Purchase</h2>

                <div className="mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h3 className="font-semibold mb-2">{app.name}</h3>
                        <p className="text-gray-600 text-sm">{app.tagline || app.description}</p>
                        <p className="text-2xl font-bold mt-2">
                            {app.pricing_model === 'free' ? (
                                'Free'
                            ) : (
                                <>
                                    ${app.price}
                                    {app.price_period && (
                                        <span className="text-base font-normal text-gray-600">/{app.price_period}</span>
                                    )}
                                </>
                            )}
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                            {error}
                        </div>
                    )}

                    {!localStorage.getItem('token') ? (
                        <div className="space-y-4">
                            <div>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="checkout-type"
                                        checked={!isGuest}
                                        onChange={() => setIsGuest(false)}
                                        className="w-4 h-4 text-blue-600"
                                    />
                                    <span>Login to existing account</span>
                                </label>
                            </div>
                            
                            <div>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="checkout-type"
                                        checked={isGuest}
                                        onChange={() => setIsGuest(true)}
                                        className="w-4 h-4 text-blue-600"
                                    />
                                    <span>Continue as guest</span>
                                </label>
                            </div>

                            {isGuest && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="your@email.com"
                                            required
                                        />
                                    </div>
                                    <p className="text-sm text-gray-500 mt-2">
                                        We'll create an account for you and send your license details to this email.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-gray-600">
                                You're logged in and ready to checkout!
                            </p>
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    {!localStorage.getItem('token') && !isGuest ? (
                        <button
                            onClick={handleLogin}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition"
                        >
                            Login →
                        </button>
                    ) : (
                        <button
                            onClick={handleCheckout}
                            disabled={loading || (isGuest && !email)}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <span>Processing...</span>
                            ) : (
                                <>
                                    <CreditCard className="w-5 h-5" />
                                    Proceed to Payment
                                </>
                            )}
                        </button>
                    )}

                    <button
                        onClick={onClose}
                        className="w-full py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                </div>

                <div className="mt-6 border-t pt-4">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                        <Lock className="w-4 h-4" />
                        <span>Secure checkout powered by Stripe</span>
                    </div>
                </div>
            </div>
        </div>
    );
}